import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertTriangle, CheckCircle, Clock, Zap, HeartPulse } from "lucide-react";

const HealthStatusBadge = ({ status }) => {
  const styles = {
    healthy: "bg-emerald-100 text-emerald-700 border-emerald-200",
    monitor: "bg-amber-100 text-amber-700 border-amber-200",
    concern: "bg-orange-100 text-orange-700 border-orange-200",
    warning: "bg-red-100 text-red-700 border-red-200",
  };
  
  const icons = {
    healthy: <CheckCircle size={12} className="mr-1" />,
    monitor: <Clock size={12} className="mr-1" />,
    concern: <AlertTriangle size={12} className="mr-1" />,
    warning: <AlertTriangle size={12} className="mr-1" />,
  };
  
  return (
    <Badge className={`text-sm ${styles[status] || styles.healthy}`}>
      {icons[status]} {status?.toUpperCase()}
    </Badge>
  );
};

const ComponentRow = ({ name, data }) => {
  if (!data) return null;
  
  return (
    <div className="border-b border-slate-100 last:border-0 py-3 px-4 hover:bg-slate-50/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-slate-800">{name}</span>
          <HealthStatusBadge status={data.health_status} />
        </div>
        <span className="text-sm font-mono text-slate-400">{data.frequency_range}</span>
      </div>
      <p className="text-sm text-slate-500 mb-2">{data.description}</p>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-slate-400">Mean Energy:</span>
          <span className="ml-1 font-mono text-slate-700">{data.mean_energy?.toFixed(4)}</span>
        </div>
        <div>
          <span className="text-slate-400">Variability:</span>
          <span className="ml-1 font-mono text-slate-700">{data.variability_percent?.toFixed(1)}%</span>
        </div>
        <div>
          <span className="text-slate-400">Anomalies:</span>
          <span className={`ml-1 font-mono ${data.anomaly_count > 0 ? 'text-amber-600' : 'text-slate-700'}`}>
            {data.anomaly_count}
          </span>
        </div>
      </div>
      {data.healthy_signature && (
        <p className="text-sm text-emerald-600 mt-2 italic">
          Healthy signature: {data.healthy_signature}
        </p>
      )}
    </div>
  );
};

const AnomalyTimeline = ({ anomalies }) => {
  if (!anomalies || anomalies.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400 text-sm">
        <CheckCircle size={24} className="mx-auto mb-2 text-emerald-500" />
        No significant anomalies detected
      </div>
    );
  }
  
  return (
    <div className="space-y-2 max-h-[300px] overflow-y-auto">
      {anomalies.map((anomaly, idx) => (
        <div 
          key={idx} 
          className={`flex items-center gap-3 p-3 rounded-md border ${
            anomaly.severity === 'high' 
              ? 'bg-red-50 border-red-200' 
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex-shrink-0">
            <Clock size={14} className="text-slate-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-base font-bold">{anomaly.time}s</span>
              <Badge className={`text-sm ${
                anomaly.severity === 'high' 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {anomaly.severity}
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mt-0.5">
              <strong>{anomaly.component}</strong> - {anomaly.type?.replace('_', ' ')} at {anomaly.frequency_range}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdvancedDiagnostic({ spectrogramDiagnostic, spectrogramImage, hasTurbo = false }) {
  // Don't render if there's no data or there's an error
  if (!spectrogramDiagnostic || spectrogramDiagnostic.error) {
    return null;
  }

  const rawComponentAnalysis = spectrogramDiagnostic.component_analysis || {};
  const rawAnomalyEvents = spectrogramDiagnostic.anomaly_events || [];
  const summary = spectrogramDiagnostic.summary || {};
  
  // Filter out turbo components for non-turbo vehicles
  const componentAnalysis = {};
  Object.entries(rawComponentAnalysis).forEach(([key, value]) => {
    if (!hasTurbo && key.toLowerCase().includes('turbo')) {
      return; // Skip turbo for non-turbo vehicles
    }
    componentAnalysis[key] = value;
  });
  
  // Filter out turbo-related anomalies for non-turbo vehicles
  const anomalyEvents = rawAnomalyEvents.filter(a => {
    if (!hasTurbo && (a.component || '').toLowerCase().includes('turbo')) {
      return false;
    }
    return true;
  });

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="advanced-diagnostic-card">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse size={16} className="text-indigo-600" />
          <span className="text-base font-bold uppercase tracking-wider text-indigo-800">
            Advanced Engine Diagnostic
          </span>
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-sm">
            ECG-LIKE ANALYSIS
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-mono text-indigo-600">
            Duration: {summary.total_duration}s
          </span>
          <span className="text-sm font-mono text-indigo-600">
            Components: {summary.components_analyzed}
          </span>
          <span className={`text-sm font-mono ${summary.total_anomalies > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            Anomalies: {summary.total_anomalies}
          </span>
        </div>
      </div>
      
      <CardContent className="p-4">
        <Tabs defaultValue={spectrogramImage ? "spectrogram" : "components"} className="w-full">
          <TabsList className="bg-slate-100 rounded-sm h-8 mb-4">
            {spectrogramImage && (
              <TabsTrigger 
                value="spectrogram" 
                className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white"
                data-testid="tab-spectrogram"
              >
                <Activity size={12} className="mr-1" /> Spectrogram
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="components" 
              className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white"
              data-testid="tab-components"
            >
              <Zap size={12} className="mr-1" /> Components
            </TabsTrigger>
            <TabsTrigger 
              value="anomalies" 
              className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white"
              data-testid="tab-anomalies"
            >
              <AlertTriangle size={12} className="mr-1" /> Anomalies
              {anomalyEvents.length > 0 && (
                <span className="ml-1 bg-amber-500 text-white text-sm px-1.5 rounded-full">
                  {anomalyEvents.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Spectrogram Image Tab */}
          {spectrogramImage && (
            <TabsContent value="spectrogram">
              <div className="relative">
                <img 
                  src={`data:image/png;base64,${spectrogramImage}`} 
                  alt="Engine Diagnostic Spectrogram"
                  className="w-full rounded-md border border-indigo-200"
                  data-testid="spectrogram-image"
                />
                <div className="absolute top-2 right-2 bg-indigo-600 text-white text-base font-bold px-2 py-1 rounded-sm uppercase">
                  Time-Frequency Analysis
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-3 text-center">
                Real-time frequency analysis showing engine component activity over time - similar to medical ECG visualization
              </p>
            </TabsContent>
          )}

          {/* Component Analysis Tab */}
          <TabsContent value="components">
            <div className="border border-slate-200 rounded-md overflow-hidden">
              {Object.entries(componentAnalysis).length > 0 ? (
                Object.entries(componentAnalysis).map(([name, data]) => (
                  <ComponentRow key={name} name={name} data={data} />
                ))
              ) : (
                <div className="text-center py-6 text-slate-400 text-sm">
                  No component analysis available
                </div>
              )}
            </div>
          </TabsContent>

          {/* Anomalies Timeline Tab */}
          <TabsContent value="anomalies">
            <AnomalyTimeline anomalies={anomalyEvents} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
